const cities = [
  { name: "Celebration", zips: "34747" },
  { name: "Kissimmee", zips: "34741, 34743" },
  { name: "Lake Buena Vista", zips: "32830" },
  { name: "Davenport", zips: "33837" },
  { name: "Clermont", zips: "34711" },
  { name: "Windermere", zips: "34786" },
  { name: "Winter Garden", zips: "34787" },
  { name: "Ocoee", zips: "34761" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or the Town Center in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function CelebrationLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Celebration &amp; South Orlando
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Celebration, Kissimmee, Lake Buena Vista, or anywhere in south Orange and Osceola County, Fast Passport Center makes expedited passport processing simple — no drive to downtown Orlando, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Celebration is unlike any other community in Florida. Conceived by The Walt Disney Company in the mid-1990s as a master-planned community that would blend traditional American small-town charm with modern urban planning, Celebration remains one of the most distinctive places to live in the Orlando area. The New England-style architecture — white picket fences, front porches, tree-lined streets with brick pavers, and a classic town center gathered around a pristine lake — creates an atmosphere that feels more like a storybook village than a suburb just 10 minutes from the world's largest theme park complex. For roughly 11,000 residents, Celebration is not just a neighborhood — it is a lifestyle built around community, walkability, and deliberate design.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Celebration's travel profile unique is its proximity to Walt Disney World and the broader Orlando tourism ecosystem. Many Celebration residents work in the hospitality and entertainment industry — at Disney parks, Universal Studios, SeaWorld, and the thousands of supporting businesses that make Orlando the most visited city in the United States. This connection to global tourism means Celebration residents travel internationally at rates far above the national average, often to Disney's international parks in Paris, Tokyo, Shanghai, and Hong Kong. Cast members travel for training, seasonal transfers, and company events. Families visit Disney's international properties for bucket-list vacations. And the constant flow of international visitors through Orlando means Celebration residents frequently host relatives from abroad, occasionally needing passport services for accompanying family members.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Celebration's demographics are notably diverse for a community of its size. The original Disney planning attracted professionals from across the country who were drawn to the innovative urban design and proximity to major employers. Today, the community includes a mix of Disney cast members, healthcare professionals (AdventHealth Celebration is one of the region's top hospitals), educators, and remote workers who value Celebration's walkable downtown and strong sense of community. Celebration K-8 and Celebration High School serve families who prioritize education and safety, while the town's downtown — with its boutique shops, lakeside restaurants, and year-round community events — creates a social fabric that feels genuinely neighborly.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The geographic position of Celebration is ideal for travelers but creates specific passport logistics. Orlando International Airport (MCO) is only 25–30 minutes northeast via World Drive and I-4, making it one of the most accessible major airports for any U.S. suburb. Port Canaveral — the world's second-busiest cruise port and home to Disney Cruise Line — is approximately 65 minutes east via the 417 and 528. But the closest regional passport agency is in Miami, roughly 230 miles south on the Florida Turnpike — a four-hour drive that no Celebration resident wants to make twice in one week. For a family of four preparing for a Disney cruise departure from Port Canaveral, discovering an expired passport the week before sailing is not just stressful — it is a logistical crisis that standard processing cannot resolve.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Celebration's travel demographics split into several overlapping groups. Disney-affiliated professionals travel to international parks and corporate events on short notice. Families plan elaborate Disney World vacations that often include extensions to Disney's international resorts in Paris, Tokyo, or Hong Kong. Medical professionals at AdventHealth Celebration attend international conferences in Europe and Asia. And the community's affluent families — drawn by Celebration's excellent schools and distinctive lifestyle — vacation in the Caribbean, Europe, and Latin America with the same frequency as other Orlando-area professionals.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Celebration travelers across every demographic — cast members flying to Shanghai Disney for training, families of five preparing for Disney cruises, medical professionals attending European conferences, and students in Celebration High School's international programs. Our courier-based expedited service means you start online from your front porch on Market Street, your office at AdventHealth, or your favorite lakeside cafe — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Orlando</p>
                  <p className="text-white/60 text-xs mt-0.5">200 S Orange Ave, Orlando, FL 32801</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Serving Celebration — approximately 25 miles north via I-4 to downtown Orlando
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Osceola and Orange County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your Celebration home in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location in Kissimmee or Orlando, or <strong className="text-white">schedule a drop-off</strong> at our Orlando office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Osceola and south Orange County — start online, ship overnight, and we handle the rest. No need to drive to downtown Orlando.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Orlando", href: "/orlando" },
                  { name: "Windermere", href: "/windermere" },
                  { name: "Winter Garden", href: "/winter-garden" },
                  { name: "Winter Park", href: "/winter-park" },
                  { name: "Lake Mary", href: "/lake-mary" },
                  { name: "Tampa", href: "/tampa" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}